import { Injectable } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';
import { ClientSettingsService, ModePermissions } from '../../../services/client-settings.service';
import {
  Customer,
  OfferGroupService,
  PricingGroup,
} from '../../../services/offer-group.service';
import { ProductDbService } from '../../../services/productdb.service';
import { Product, ProductHttpResponse } from '../../../modals/product.modals';

/**
 * Shared dependency bag for the offer/pricing-group mode cluster.
 *
 * Nearly every method here has an async gap (subscribe/await/setTimeout)
 * before reading component state or calling a sibling method, so — unlike
 * the simpler modules — EVERY piece of state is a live getter/setter
 * function rather than a plain snapshot value. That way a value read deep
 * inside a nested `.subscribe(() => ... .then(() => ... setTimeout(...)))`
 * chain always reflects the component's current state at the moment it
 * actually runs, matching the original `this.x` semantics exactly regardless
 * of how deep the callback nesting is.
 *
 * Methods NOT in this module's list (updateFormDataWithQuotationMode,
 * getProducts, DiscountchangeCalc) are exposed as plain callbacks into the
 * component's existing methods.
 */
export interface OfferGroupModeDeps {
  getPricingGroups: () => PricingGroup[];
  setPricingGroups: (value: PricingGroup[]) => void;

  getModePermissions: () => ModePermissions;

  getIsOfferGroupMode: () => boolean;
  setIsOfferGroupMode: (value: boolean) => void;

  getMobileNumber: () => string;

  getCustomerMobile: () => string;
  setCustomerMobile: (value: string) => void;

  getHasCustomerOverride: () => boolean;

  setShowOfferGroupCustomerDialog: (value: boolean) => void;

  getOfferGroupMobileAutocomplete: () => AutoComplete;

  getSelectedCustomer: () => Customer | null;
  setSelectedCustomer: (value: Customer | null) => void;

  getSelectedOfferGroupId: () => number | null;
  setSelectedOfferGroupId: (value: number | null) => void;

  getLastSelectedOfferGroupId: () => number | null;
  setLastSelectedOfferGroupId: (value: number | null) => void;

  getIsExchangeOrder: () => boolean;

  getIsQuotationMode: () => boolean;
  setIsQuotationMode: (value: boolean) => void;

  getIsWholesaleMode: () => boolean;
  setIsWholesaleMode: (value: boolean) => void;

  getIsSuspenseMode: () => boolean;
  setIsSuspenseMode: (value: boolean) => void;

  getCartProducts: () => Product[];
  setProductsTemp: (value: Partial<Product[]>) => void;

  getIsSalesDiscountEditEnabled: () => boolean;

  getItemId: () => any;

  getCustomers: () => Customer[];
  setFilteredCustomersForOfferGroup: (value: Customer[]) => void;

  ensureCustomersLoaded: () => Promise<void>;
  updateFormDataWithQuotationMode: () => void;
  searchProducts: (searchText: string) => void;
  discountChangeCalc: () => void;
}

/**
 * OfferGroupModeService
 *
 * Dedicated service for offer/pricing-group mode: enabling/disabling the
 * mode, loading pricing groups, re-pricing existing cart items against a
 * selected group, and the customer-lookup-by-mobile flow used to
 * auto-select a customer's pricing group.
 *
 * Named `OfferGroupModeService` (not `OfferGroupService`) to avoid colliding
 * with the existing `OfferGroupService` at src/services/offer-group.service.ts,
 * which this service injects directly for the actual customer/pricing-group
 * data calls (getCustomers, getPricingGroups, setOfferGroupMode, etc.) —
 * same "inject the real singleton" pattern used in CustomerLookupService.
 *
 * Extracted from DashboardComponent to decouple offer-group-mode logic
 * while preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class OfferGroupModeService {
  constructor(
    private readonly authService: AuthService,
    private readonly messageService: MessageService,
    private readonly clientSettingsService: ClientSettingsService,
    private readonly offerGroupService: OfferGroupService,
    private readonly productService: ProductDbService,
  ) {}

  async loadPricingGroups(deps: OfferGroupModeDeps): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    try {
      deps.setPricingGroups(
        (await this.offerGroupService.getPricingGroups().toPromise()) || [],
      );
    } catch (error) {
      console.error('Failed to load pricing groups:', error);
      if (this.authService.isAuthenticated()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load pricing groups',
          life: 3000,
        });
      }
    }
  }

  // Toggle Offer Group Mode (Alt+O)
  async toggleOfferGroupMode(deps: OfferGroupModeDeps): Promise<void> {
    this.clientSettingsService.loadClientSettings().subscribe(() => {
      if (!deps.getModePermissions().pricingGroupMode) {
        this.messageService.add({
          severity: 'error',
          summary: 'Access Denied',
          detail: 'Pricing group mode is not enabled for your account',
          life: 3000,
        });
        return;
      }

      if (!deps.getIsOfferGroupMode()) {
        deps.ensureCustomersLoaded().then(() => {
          // Populate customerMobile from header's mobileNumber if available
          if (deps.getMobileNumber() && !deps.getCustomerMobile()) {
            deps.setCustomerMobile(deps.getMobileNumber());
          }

          if (!deps.getCustomerMobile() && !deps.getHasCustomerOverride()) {
            deps.setShowOfferGroupCustomerDialog(true);
            setTimeout(() => {
              const offerGroupMobileAutocomplete =
                deps.getOfferGroupMobileAutocomplete();
              if (offerGroupMobileAutocomplete && offerGroupMobileAutocomplete.el) {
                const inputElement =
                  offerGroupMobileAutocomplete.el.nativeElement.querySelector(
                    'input',
                  );
                if (inputElement) {
                  inputElement.focus();
                  inputElement.select();
                }
              }
            }, 150);
          } else {
            if (deps.getCustomerMobile()) {
              this.onMobileEntered(deps);
            }
            this.enableOfferGroupMode(deps);
          }
        });
      } else {
        this.disableOfferGroupMode(deps);
      }
    });
  }

  // Update existing cart items with offer prices from the selected pricing group
  updateExistingItemsWithOfferPrices(
    groupId: number,
    deps: OfferGroupModeDeps,
  ): void {
    const products = deps.getCartProducts();
    if (!products || products.length === 0) {
      return;
    }

    // Collect all barcodes from existing cart items
    const barcodes = products
      .map((product) => product.psid)
      .filter((barcode) => barcode && barcode.trim() !== '');

    if (barcodes.length === 0) {
      console.log('No valid barcodes found, skipping offer price update');
      return;
    }

    // Call API to get offer prices for these barcodes
    this.productService.getSellingGroupItems(groupId, barcodes).subscribe({
      next: (response: ProductHttpResponse) => {
        if (
          response.code === '200' &&
          response.data &&
          response.data.length > 0
        ) {
          const offerPriceMap = new Map<string, Product>();
          response.data.forEach((item) => {
            offerPriceMap.set(item.psid, item);
          });

          // Update existing cart items with offer prices
          deps.getCartProducts().forEach((cartItem) => {
            const offerItem = offerPriceMap.get(cartItem.psid);
            if (offerItem) {
              // Store original price if not already stored
              if (
                !cartItem.barcodesellprice ||
                cartItem.barcodesellprice === 0
              ) {
                cartItem.barcodesellprice = cartItem.sellingprice;
              }

              // Update with grouping price if available, otherwise use sellingprice from API
              if (offerItem.groupingprice && offerItem.groupingprice > 0) {
                cartItem.groupingprice = offerItem.groupingprice;
                cartItem.sellingprice = offerItem.groupingprice;
              } else if (offerItem.offerprice && offerItem.offerprice > 0) {
                cartItem.offerprice = offerItem.offerprice;
                cartItem.sellingprice = offerItem.offerprice;
              } else {
                cartItem.sellingprice = offerItem.sellingprice;
              }
            }
          });

          // Only recalculate if user can edit discounts
          if (deps.getIsSalesDiscountEditEnabled()) {
            deps.discountChangeCalc();
          }
        }
      },
      error: (error) => {
        console.error('Error fetching offer prices for existing items:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update prices for existing items',
          life: 3000,
        });
      },
    });
  }

  revertExistingItemsToOriginalPrices(deps: OfferGroupModeDeps): void {
    const products = deps.getCartProducts();
    if (!products || products.length === 0) {
      return;
    }

    products.forEach((cartItem) => {
      if (cartItem.barcodesellprice && cartItem.barcodesellprice > 0) {
        cartItem.sellingprice = cartItem.barcodesellprice;
      }
      cartItem.groupingprice = 0;
    });

    // Only recalculate if user can edit discounts
    if (deps.getIsSalesDiscountEditEnabled()) {
      deps.discountChangeCalc();
    }
  }

  async enableOfferGroupMode(deps: OfferGroupModeDeps): Promise<void> {
    if (deps.getIsExchangeOrder()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot Switch Mode',
        detail: 'Please complete or cancel the exchange order first',
        life: 3000,
      });
      return;
    }

    deps.setIsOfferGroupMode(true);
    this.offerGroupService.setOfferGroupMode(true);

    if (deps.getIsQuotationMode()) {
      deps.setIsQuotationMode(false);
    }
    if (deps.getIsWholesaleMode()) {
      deps.setIsWholesaleMode(false);
    }
    if (deps.getIsSuspenseMode()) {
      deps.setIsSuspenseMode(false);
      deps.getCartProducts().forEach((product) => {
        delete product.isgst;
        product.selected = false;
      });
      deps.setProductsTemp(deps.getCartProducts().map((product) => ({ ...product })));
    }

    if (deps.getPricingGroups().length === 0) {
      await this.loadPricingGroups(deps);
    }

    // Ensure a pricing group is selected (from last selection, customer's group, or default to first)
    if (deps.getLastSelectedOfferGroupId()) {
      deps.setSelectedOfferGroupId(deps.getLastSelectedOfferGroupId());
      this.offerGroupService.setSelectedGroup(deps.getSelectedOfferGroupId());
    } else if (
      deps.getSelectedCustomer() &&
      deps.getSelectedCustomer()!.sellingpricegroup_id
    ) {
      this.autoSelectPricingGroup(deps.getSelectedCustomer()!, deps);
    } else {
      this.autoSelectFirstPricingGroup(false, deps);
    }

    if (deps.getSelectedOfferGroupId()) {
      this.updateExistingItemsWithOfferPrices(deps.getSelectedOfferGroupId()!, deps);
    } else {
      // Only recalculate if user can edit discounts
      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }
    }

    deps.updateFormDataWithQuotationMode();

    const currentSearchText =
      typeof deps.getItemId() === 'string' ? deps.getItemId() : '';
    if (currentSearchText && currentSearchText.trim() !== '') {
      deps.searchProducts(currentSearchText);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Offer Group Mode Enabled',
      detail: 'Press Alt+O to switch back to normal mode',
      life: 2000,
    });
  }

  disableOfferGroupMode(deps: OfferGroupModeDeps): void {
    deps.setIsOfferGroupMode(false);
    deps.setLastSelectedOfferGroupId(deps.getSelectedOfferGroupId());
    deps.setSelectedOfferGroupId(null);
    this.offerGroupService.setOfferGroupMode(false);

    this.revertExistingItemsToOriginalPrices(deps);

    deps.updateFormDataWithQuotationMode();

    const currentSearchText =
      typeof deps.getItemId() === 'string' ? deps.getItemId() : '';
    if (currentSearchText && currentSearchText.trim() !== '') {
      deps.searchProducts(currentSearchText);
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Normal Mode Enabled',
      detail: 'Press Alt+O to switch to offer group mode',
      life: 2000,
    });
  }

  // Active Methods & Handlers for Offer Group Customer Dialog Component
  async filterCustomersForOfferGroup(
    event: any,
    deps: OfferGroupModeDeps,
  ): Promise<void> {
    await deps.ensureCustomersLoaded();

    const query = event.query.toLowerCase();

    deps.setFilteredCustomersForOfferGroup(
      deps.getCustomers().filter(
        (customer) =>
          customer.mobile_no.includes(query) ||
          customer.customer_name.toLowerCase().includes(query),
      ),
    );
  }

  onOfferGroupFilterCustomers(event: any, deps: OfferGroupModeDeps): void {
    this.filterCustomersForOfferGroup(event, deps);
  }

  onOfferGroupMobileSelect(event: any, deps: OfferGroupModeDeps): void {
    const customer = event.value || event;
    if (customer && customer.mobile_no) {
      deps.setCustomerMobile(customer.mobile_no);
      deps.setSelectedCustomer(customer);
      this.autoSelectPricingGroup(customer, deps);
      this.messageService.clear();
    }
  }

  onMobileEntered(deps: OfferGroupModeDeps): void {
    const mobileToSearch = deps.getCustomerMobile() || deps.getMobileNumber();

    if (mobileToSearch && mobileToSearch.length >= 10) {
      const customer = this.offerGroupService.findCustomerByMobile(
        deps.getCustomers(),
        mobileToSearch,
      );
      if (customer) {
        deps.setSelectedCustomer(customer);
        this.autoSelectPricingGroup(customer, deps);

        this.messageService.clear();
      } else {
        deps.setSelectedCustomer(null);

        this.autoSelectFirstPricingGroup(true, deps);

        this.messageService.add({
          severity: 'warn',
          summary: 'Customer Not Found',
          detail: 'No customer found with this mobile number',
          life: 2000,
        });
      }
    } else {
      deps.setSelectedCustomer(null);

      this.autoSelectFirstPricingGroup(false, deps);

      this.messageService.clear();
    }
  }

  private autoSelectPricingGroup(customer: Customer, deps: OfferGroupModeDeps): void {
    if (customer.sellingpricegroup_id) {
      const matchingGroup = deps
        .getPricingGroups()
        .find((group) => group.id === customer.sellingpricegroup_id);
      if (matchingGroup) {
        deps.setSelectedOfferGroupId(matchingGroup.id);
        this.offerGroupService.setSelectedGroup(matchingGroup.id);
        this.messageService.add({
          severity: 'success',
          summary: 'Pricing Group Selected',
          detail: `Auto-selected: ${matchingGroup.name}`,
          life: 2000,
        });
      }
    }
  }

  private autoSelectFirstPricingGroup(
    showMessage: boolean = true,
    deps: OfferGroupModeDeps,
  ): void {
    const pricingGroups = deps.getPricingGroups();
    if (pricingGroups && pricingGroups.length > 0) {
      const firstGroup = pricingGroups[0];
      deps.setSelectedOfferGroupId(firstGroup.id);
      this.offerGroupService.setSelectedGroup(firstGroup.id);

      if (showMessage) {
        this.messageService.add({
          severity: 'info',
          summary: 'Default Pricing Group',
          detail: `Selected: ${firstGroup.name}`,
          life: 2000,
        });
      }
    } else {
      deps.setSelectedOfferGroupId(null);
      this.offerGroupService.setSelectedGroup(null);
    }
  }

  onOfferGroupSelected(deps: OfferGroupModeDeps): void {
    this.offerGroupService.setSelectedGroup(deps.getSelectedOfferGroupId());

    if (deps.getIsOfferGroupMode()) {
      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }
    }
  }
}
